import React, { forwardRef, ReactNode, useEffect, useState } from 'react';

import '../../../_flexCharge/assets/sass/core/components/forms/FormInput.scss';
import { CheckPicker, DatePicker, DateRangePicker, SelectPicker } from 'rsuite';
import { KTSVG, toAbsoluteUrl } from '../../utils';
import { Calendar } from 'react-date-range';
import clsx from 'clsx';
import { Editor } from './Editor';
import { endOfDay, isAfter } from 'date-fns';
import './FormInput.scss';
import { RenderExtraFooter } from '../filter/RenderExtraFooter';
import { DateRange } from 'rsuite/DateRangePicker';
import { useTranslation } from 'react-i18next';
import { ItemDataType } from 'rsuite/CascadeTree';
import { WebsiteInput } from './WebsiteInput';
import PhoneInputComponent from '../form/PhoneInputComponent';
import { FakeInput } from '../form/FakeInput';
import {
	ErrorMessagePosition,
	formInputFieldName,
	HELPERS_ROUTES,
	InputType,
	PickerData
} from '../form/form-input-constants';
import { getTitle } from '../form/FakeInputHelper';
import { TypeAttributes } from 'rsuite/cjs/internals/types';
import { DefaultInput } from '../form/DefaultInput';

export interface IFormInputProps {
	formik?: any;
	fieldName: formInputFieldName;
	label?: string;
	inputType: InputType | string; // TODO should be only InputType
	additionalClasses?: string;
	labelAdditionalClasses?: string;
	fakeInputLabelAdditionalClasses?: string;
	placeholder?: string;
	disabled?: boolean;
	required?: boolean;
	data?: PickerData[];
	isEditMode?: boolean;
	searchable?: boolean;
	enableCreateNew?: boolean;
	setCreateNew?: any;
	errorMessagePosition?: ErrorMessagePosition;
	placement?: TypeAttributes.Placement;
	cleanable?: boolean;
	readOnly?: boolean;
	maskedFieldName?: string;
	dataCy?: string;
	picker?: any;
	allValue?: string [];
	link?: string;
	renderMenu?: (menu: React.ReactElement) => React.ReactElement;
	searchBy?: (keyword: string, label: ReactNode, item: ItemDataType<string | number>) => boolean;
	renderMenuItem?: (label: React.ReactNode, item: ItemDataType<string | number>) => React.ReactNode;
	defaultCountry?: any;
	fakeInputAdditionalClass?: string;
	isTimePicker?: boolean;
	fieldsToDefault?: string[]; // fields that should be set to default value after each current changes,
	isLabelIcon?: boolean;
	isFakeInputValueTooltip?: boolean;
	linkAdditionalClassName?: string;
	tooltipText?: string;
	defaultDate?: DateRange;
	tooltipPlacement?: TypeAttributes.Placement;
	suffix?: string;
	prefix?: string; // New property for prefix
	// https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
	autoComplete?: string;
	key?: string;
	emptyValue?: boolean;
	route?: HELPERS_ROUTES;
	ref?: any,
	showPassword?: boolean;
	setShowPassword?: (value: boolean) => void;
	isPasswordShown?: boolean;
	isPasswordStrengthShown?: boolean;
	showLink?: boolean;
	descriptionFieldHeight?: number;
	min?: number;
	max?: number;
	useNumberFormatter?: boolean; // Property to enable special number formatting
	shouldFormat?: boolean; // New property to enable comma formatting in edit mode
}

// eslint-disable-next-line react/display-name
export const FormInput = forwardRef<any, IFormInputProps>((props, ref): JSX.Element => {
  const {
    formik,
    fieldName,
    placeholder,
    label,
    inputType,
    additionalClasses,
    labelAdditionalClasses,
    maskedFieldName,
    disabled,
    required,
    data,
    placement = 'bottomStart',
    isEditMode = true,
    searchable = true,
    enableCreateNew,
    cleanable,
    readOnly,
    // picker,
    allValue,
    link,
    fakeInputAdditionalClass,
    fakeInputLabelAdditionalClasses,
    renderMenu,
    searchBy,
    renderMenuItem,
    defaultCountry = 'us',
    isTimePicker,
    errorMessagePosition = {
      top: '4.9rem',
      left: '0.7rem',
      right: 'unset',
      bottom: 'unset'
    },
    dataCy,
    fieldsToDefault,
    isLabelIcon = false,
    tooltipText,
    isFakeInputValueTooltip = false,
    defaultDate,
    tooltipPlacement,
    linkAdditionalClassName,
    suffix,
    autoComplete,
    showLink,
	  descriptionFieldHeight = 130,
  } = props;
  const { t } = useTranslation();

  const { top = '4.9rem', left = '0.6rem', right = 'unset', bottom = 'unset' } = errorMessagePosition;

  const [selectPickerData, setSelectPickerData] = useState(data ?? []);
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);

	useEffect(() => {
		if (data?.length) {
			setSelectPickerData(required ? data : [{ value: '', label: 'Not selected' }, ...data]);
		}
	}, [data]);

  const { value } = formik.getFieldProps(fieldName);

  let maskedValue;
  if (maskedFieldName) {
    maskedValue = formik.getFieldProps(maskedFieldName).value;
  }

	if (!isEditMode) {
		let pickerValue = '';
		if (inputType === 'select' && selectPickerData.length) {
			const pickerItem = selectPickerData?.find(item => item.value === value);
			if (pickerItem) {
				pickerValue = pickerItem.label;
			}
		}
		return (
			<FakeInput
				value={pickerValue || (value ?? maskedValue)}
				label={label}
				type={inputType}
				additionalClasses={additionalClasses}
				tooltipText={tooltipText}
				isLabelIcon={isLabelIcon}
				isValueTooltip={isFakeInputValueTooltip}
				link={link}
				fakeInputAdditionalClass={fakeInputAdditionalClass}
				fakeInputLabelAdditionalClasses={fakeInputLabelAdditionalClasses}
				linkAdditionalClassName={linkAdditionalClassName}
				suffix={suffix}
				prefix={props.prefix}
				autoComplete={autoComplete ?? 'Off'}
				useNumberFormatter={props.useNumberFormatter}
			/>
		);
	}

  const fieldLabel = required
    ? (
      <>
        <span className={clsx('me-1', labelAdditionalClasses)}>{label}</span>
        <span className="text-danger">*</span>
      </>
    )
    : <span className={clsx(labelAdditionalClasses)}>{label}</span>;


	switch (inputType) {
		case 'select': {
			return (
				<div className={clsx('col-6 col-sm-4 col-md-3 fv-row input-container mb-5', additionalClasses)}>
					{getTitle({ label, fieldLabel, isLabelIcon, tooltipText, tooltipPlacement, t })}
					<SelectPicker
						id={fieldName}
						className="p-0 select-picker pt-1"
						data={selectPickerData}
						placeholder={placeholder ? `Select ${placeholder}` : ''}
						name={fieldName}
						value={value || ''}
						placement={placement}
						onChange={(selectorValue): void => {
							formik.setFieldValue(fieldName, selectorValue);

              if (fieldsToDefault) {
                fieldsToDefault?.forEach(name => {
                  formik.setFieldValue(name, formik.initialValues[name]);
                });
              }
            }}
            onClean={(): void => formik.setFieldValue(fieldName, '')}
            disabled={Boolean(disabled)}
            searchable={searchable}
            cleanable={cleanable}
            renderMenu={renderMenu}
            renderMenuItem={renderMenuItem}
            searchBy={searchBy}
            data-cy={dataCy || ''}
          />
          {formik.touched[fieldName] && formik.errors[fieldName] && (
            <div className="error-container">
              <div className="text-danger fs-8">{formik.errors[fieldName]}</div>
            </div>
          )}
          {enableCreateNew && (
            <div className="error-container" onClick={(): void => props.setCreateNew(true)}>
              <div className="fs-8 cursor-pointer">
                <u>
                  Create new +
                </u>
              </div>
            </div>
          )}

				</div>
			);
		}
		case 'multiSelect': {
			return (
				<div className={clsx('col-6 col-sm-4 col-md-3 fv-row input-container', additionalClasses)}>
					{getTitle({ label, fieldLabel, isLabelIcon, tooltipText, tooltipPlacement, t, className: 'mb-2' })}

					<CheckPicker
						data={selectPickerData}
						placeholder={placeholder ? `Select ${placeholder}` : ''}
						className="select-picker"
						placement={placement}
						name={fieldName}
						ref={ref}
						searchable={searchable}
						value={value || []}
						onChange={(value): void => formik.setFieldValue(fieldName, value)}
						onClean={(): void => formik.setFieldValue(fieldName, [])}
						renderExtraFooter={(): JSX.Element => (
							<RenderExtraFooter
								statusFilter={value}
								allValue={allValue}
								handleCheckAll={(value, checked): void => {
									formik.setFieldValue(fieldName, checked ? allValue : []);
								}}
								// handleSave={() => picker.current?.close()}
							/>
						)}
					/>
					{formik.touched[fieldName] && formik.errors[fieldName] && (
						<div className="error-container">
							<div className="text-danger fs-8">{formik.errors[fieldName]}</div>
						</div>
					)}
				</div>
			);
		}
		case 'date': {
			return (
				<div className={clsx('form-input-picker-container col-6 col-sm-4 col-md-3 fv-row mb-5', additionalClasses)}>
					{getTitle({ label, fieldLabel, isLabelIcon, tooltipText, tooltipPlacement, t })}
					<DatePicker
						value={value}
						format={isTimePicker ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd'}
						placeholder={placeholder ? `Select ${placeholder}` : ''}
						onChange={(dateValue): void => {
							formik.setFieldValue(fieldName, new Date(dateValue));
						}}
						onClean={(): void => {
							formik.setFieldValue(fieldName, null);
						}}
						className="w-100 date-picker-custom"
						shouldDisableDate={(date): boolean => isAfter(date, endOfDay(new Date()))}
						placement={placement}
						oneTap
					/>
					{formik.touched[fieldName] && formik.errors[fieldName] && (
						<div className="error-container">
							<div className="text-danger fs-8">{formik.errors[fieldName]}</div>
						</div>
					)}
				</div>
			);
		}
		case 'date-range': {
			return (
				<div className={clsx('form-input-picker-container col-6 col-sm-4 col-md-3 fv-row mb-10', additionalClasses)}>
					{getTitle({ label, fieldLabel, isLabelIcon, tooltipText, tooltipPlacement, t })}
					<DateRangePicker
						value={value?.length ? value : null}
						defaultCalendarValue={defaultDate}
						format={isTimePicker ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd'}
						placeholder={placeholder ? `Select ${placeholder}` : ''}
						onChange={(dateValue): void => formik.setFieldValue(fieldName, dateValue)}
						onClean={(): void => formik.setFieldValue(fieldName, [])}
						className="w-100 date-picker-custom"
						shouldDisableDate={(date): boolean => isAfter(date, endOfDay(new Date()))}
						placement={placement}
					/>
					{formik.touched[fieldName] && formik.errors[fieldName] && (
						<div className="error-container">
							<div className="text-danger fs-8">{formik.errors[fieldName]}</div>
						</div>
					)}
				</div>
			);
		}
		case 'description': {
			return (
				<div className={clsx('fv-row input-container mb-5', additionalClasses)}>
					{getTitle({ label, fieldLabel, isLabelIcon, tooltipText, tooltipPlacement, t })}
					<textarea
						className={clsx('form-control form-control-sm form-control-solid mb-3', disabled && 'cursor-not-allowed')}
						placeholder={placeholder}
						disabled={Boolean(disabled)}
						style={{ height: descriptionFieldHeight, resize: 'none' }}
						{...formik.getFieldProps(fieldName)}
						value={formik.values[fieldName] ?? ''} // Ensure value is not null
					/>
					{formik.touched[fieldName] && formik.errors[fieldName] && (
						<div className="error-container" style={{ top: '12rem' }}>
							<div className="text-danger fs-8">{formik.errors[fieldName]}</div>
						</div>
					)}
				</div>
			);
		}
		case 'img': {
			return (
				<div className={clsx('form-input-picker-container col-6 col-sm-4 col-md-3', additionalClasses)}>
					<div className="w-100 d-flex align-items-center justify-content-end select-date-button text-center"
						 onClick={(): void => setIsDatePickerVisible(!isDatePickerVisible)}
					>
						{
							value
								? value
								: 'Select Date'
						}
						<KTSVG
							path={toAbsoluteUrl('/media/icons/calendar-icon.svg')}
							className="svg-class ms-2"
						/>
					</div>
					{
						isDatePickerVisible && (
							<>
								{/*@ts-ignore*/}
								<Calendar
									date={value}
									onChange={(dateValue): void => formik.setFieldValue(fieldName, dateValue)}
									weekStartsOn={1}
									className="date-picker"
								/>
								<div className="picker-backdrop" onClick={(): void => setIsDatePickerVisible(false)}/>
							</>
						)
					}

				</div>
			);
		}
		case 'editor': {
			return (
				<Editor
					formik={formik}
					editorPlaceholder={placeholder}
					fieldLabel={fieldLabel}
					fieldName={fieldName}
					value={value}
					showLink={showLink}
				/>
			);
		}
		case 'phone': {
			return (
				<PhoneInputComponent
					label={getTitle({ label, fieldLabel, isLabelIcon, tooltipText, tooltipPlacement, t })}
					formik={formik}
					fieldName={fieldName}
					placeholder={placeholder}
					additionalClasses={additionalClasses}
					top={top}
					left={left}
					right={right}
					bottom={bottom}
					defaultCountry={defaultCountry}
				/>
			);
		}
		case 'website': {
			const value = formik.values?.[fieldName];
			const onChange = (url: string): void => {
				formik.setFieldValue(fieldName, url);
			};
			return (
				<div className={clsx('col-6 col-sm-4 col-md-3 fv-row input-container mb-5', additionalClasses)}>
					{getTitle({ label, fieldLabel, isLabelIcon, tooltipText, tooltipPlacement, t })}
					<WebsiteInput
						value={value}
						onChangeCallback={onChange}
						fieldName={fieldName}
						placeholder={placeholder}
						disabled={disabled}
						readOnly={readOnly}
					/>
					{formik.touched[fieldName] && formik.errors[fieldName] && (
						<div className="error-container">
							<div className="text-danger fs-8">{formik.errors[fieldName]}</div>
						</div>
					)}
				</div>
			);
		}
		default: {
			return (
				<DefaultInput
					formik={formik}
					fieldName={fieldName}
					inputType={inputType}
					placeholder={placeholder}
					disabled={disabled}
					readOnly={readOnly}
					autoComplete={autoComplete}
					min={props.min}
					max={props.max}
					shouldFormat={props.shouldFormat}
					suffix={suffix}
					prefix={props.prefix}
					label={label}
					additionalClasses={additionalClasses}
					isLabelIcon={isLabelIcon}
					tooltipText={tooltipText}
					tooltipPlacement={tooltipPlacement}
					errorMessagePosition={errorMessagePosition}
					fieldLabel={fieldLabel}
				/>
			);
		}
	}
});
